import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { getWerkUrenDag, dagKeyVanDatum, berekenUrenMetPauze } from '../utils/datum';

const DAG_LABELS = { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr' };
const DAG_FULL = { ma: 'Maandag', di: 'Dinsdag', wo: 'Woensdag', do: 'Donderdag', vr: 'Vrijdag' };

// Timeline range 07:00 - 19:00 (720 min)
const TL_START = 7 * 60;
const TL_END = 19 * 60;
const TL_RANGE = TL_END - TL_START;
const PAUZE_S = 12 * 60 + 30;
const PAUZE_E = 13 * 60;
const SNAP = 15; // snap to 15 min

function minToTime(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
}
function timeToMin(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}
function snap(m) { return Math.round(m / SNAP) * SNAP; }
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
function pctOf(m) { return ((m - TL_START) / TL_RANGE) * 100; }

function getWeekStart(date) {
  const d = new Date(date); d.setHours(12,0,0,0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d;
}
function fmtDate(d) { return d.toISOString().split('T')[0]; }
function fmtDK(iso) { return new Date(iso+'T12:00:00').toLocaleDateString('nl-NL',{day:'numeric',month:'short'}); }
function getISOWeek(d) {
  const dt = new Date(d); dt.setHours(0,0,0,0);
  dt.setDate(dt.getDate()+3-(dt.getDay()+6)%7);
  const w1 = new Date(dt.getFullYear(),0,4);
  return 1+Math.round(((dt-w1)/864e5-3+(w1.getDay()+6)%7)/7);
}

// ====== VISUAL TIMELINE SLIDER ======
function TimelineSlider({ roosterStart, roosterEind, start, eind, onChange, disabled }) {
  const barRef = useRef(null);
  const dragRef = useRef(null);

  const rS = timeToMin(roosterStart);
  const rE = timeToMin(roosterEind);
  const aS = timeToMin(start);
  const aE = timeToMin(eind);
  const isVrij = !start && !eind;

  const getMinFromX = useCallback((clientX) => {
    if (!barRef.current) return TL_START;
    const rect = barRef.current.getBoundingClientRect();
    const pct = (clientX - rect.left) / rect.width;
    const raw = TL_START + pct * TL_RANGE;
    return snap(clamp(Math.round(raw), TL_START, TL_END));
  }, []);

  const handleDragStart = useCallback((type, e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    dragRef.current = { type, startX: clientX, origS: aS, origE: aE };

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const m = getMinFromX(cx);

      if (dragRef.current.type === 'start') {
        const newS = clamp(m, TL_START, aE - SNAP);
        onChange(minToTime(snap(newS)), minToTime(aE));
      } else if (dragRef.current.type === 'end') {
        const newE = clamp(m, aS + SNAP, TL_END);
        onChange(minToTime(aS), minToTime(snap(newE)));
      } else if (dragRef.current.type === 'bar') {
        const dx = m - getMinFromX(dragRef.current.startX);
        const dur = dragRef.current.origE - dragRef.current.origS;
        let newS = snap(dragRef.current.origS + dx);
        let newE = newS + dur;
        if (newS < TL_START) { newS = TL_START; newE = TL_START + dur; }
        if (newE > TL_END) { newE = TL_END; newS = TL_END - dur; }
        onChange(minToTime(newS), minToTime(newE));
      }
    };

    const onEnd = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  }, [disabled, aS, aE, onChange, getMinFromX]);

  // Click on empty area to set a bar there
  const handleBarClick = useCallback((e) => {
    if (disabled || dragRef.current) return;
    if (isVrij) {
      // Reset to standard rooster
      onChange(roosterStart, roosterEind);
    }
  }, [disabled, isVrij, onChange, roosterStart, roosterEind]);

  // Hour markers
  const hours = [];
  for (let h = 7; h <= 19; h += 1) {
    hours.push(h);
  }

  return (
    <div style={{ position: 'relative', height: 52, userSelect: 'none', touchAction: 'none' }}>
      {/* Timeline background */}
      <div ref={barRef} style={{
        position: 'absolute', top: 8, left: 0, right: 0, height: 36, borderRadius: 6,
        background: '#f3f2f1', cursor: disabled ? 'default' : 'pointer', overflow: 'hidden',
      }} onClick={handleBarClick}>
        {/* Rooster (standaard) achtergrond */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: `${pctOf(rS)}%`, width: `${pctOf(rE) - pctOf(rS)}%`,
          background: '#e8e6e4', borderRadius: 4,
        }} />

        {/* Pauze zone */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: `${pctOf(PAUZE_S)}%`, width: `${pctOf(PAUZE_E) - pctOf(PAUZE_S)}%`,
          background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.05) 2px, rgba(0,0,0,0.05) 4px)',
          borderLeft: '1px dashed #c8c6c4', borderRight: '1px dashed #c8c6c4',
        }} />

        {/* Actieve werkbalk */}
        {!isVrij && (
          <div
            style={{
              position: 'absolute', top: 2, bottom: 2,
              left: `${pctOf(aS)}%`, width: `${Math.max(0, pctOf(aE) - pctOf(aS))}%`,
              background: (aE - aS) > (rE - rS) ? 'linear-gradient(135deg, #00B7C3 0%, #0097A7 100%)' :
                          (aE - aS) < (rE - rS) ? 'linear-gradient(135deg, #E86E1C 0%, #D35A0A 100%)' :
                          'linear-gradient(135deg, #0078D4 0%, #005A9E 100%)',
              borderRadius: 4, cursor: disabled ? 'default' : 'grab',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            }}
            onMouseDown={(e) => handleDragStart('bar', e)}
            onTouchStart={(e) => handleDragStart('bar', e)}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.3)', whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {minToTime(aS)}–{minToTime(aE)}
            </span>
          </div>
        )}

        {/* Drag handles */}
        {!isVrij && !disabled && (
          <>
            <div style={{
              position: 'absolute', top: 0, bottom: 0, width: 14,
              left: `calc(${pctOf(aS)}% - 7px)`, cursor: 'ew-resize',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
            }}
              onMouseDown={(e) => handleDragStart('start', e)}
              onTouchStart={(e) => handleDragStart('start', e)}
            >
              <div style={{ width: 6, height: 20, borderRadius: 3, background: '#fff', boxShadow: '0 0 4px rgba(0,0,0,0.3)', border: '1px solid #c8c6c4' }} />
            </div>
            <div style={{
              position: 'absolute', top: 0, bottom: 0, width: 14,
              left: `calc(${pctOf(aE)}% - 7px)`, cursor: 'ew-resize',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
            }}
              onMouseDown={(e) => handleDragStart('end', e)}
              onTouchStart={(e) => handleDragStart('end', e)}
            >
              <div style={{ width: 6, height: 20, borderRadius: 3, background: '#fff', boxShadow: '0 0 4px rgba(0,0,0,0.3)', border: '1px solid #c8c6c4' }} />
            </div>
          </>
        )}

        {/* Vrij overlay */}
        {isVrij && !disabled && (
          <div style={{
            position: 'absolute', top: 2, bottom: 2, left: `${pctOf(rS)}%`, width: `${pctOf(rE) - pctOf(rS)}%`,
            background: 'rgba(232,110,28,0.08)', borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px dashed #E86E1C40',
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#E86E1C' }}>🏠 Vrij</span>
          </div>
        )}
      </div>

      {/* Uur markers */}
      <div style={{ position: 'absolute', top: 46, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', pointerEvents: 'none' }}>
        {hours.filter((_,i) => i % 2 === 0).map(h => (
          <span key={h} style={{ fontSize: 9, color: '#a19f9d', position: 'absolute', left: `${pctOf(h*60)}%`, transform: 'translateX(-50%)' }}>
            {h}:00
          </span>
        ))}
      </div>
    </div>
  );
}

// ====== MAIN COMPONENT ======
export default function FlexWeekplanner() {
  const {
    currentUser, saldo, feestdagen, verlofaanvragen, verlofTypes, medewerkers,
    isAdmin, isManager, canManage, indienVerlofaanvraag,
  } = useApp();

  // Manager/admin: medewerker selectie
  const flexMedewerkers = useMemo(() =>
    medewerkers.filter(m => (m.WeekFlexUren || 0) > 0 && (m.Status || 'actief') === 'actief'),
  [medewerkers]);

  const [selectedMwId, setSelectedMwId] = useState('');
  const targetMw = canManage ? (selectedMwId ? medewerkers.find(m => m.Id === selectedMwId) : null) : currentUser;
  const werkrooster = targetMw?.Werkrooster || null;
  const targetId = targetMw?.id || targetMw?.Id || currentUser?.id;

  const [weekOffset, setWeekOffset] = useState(0);
  const [aanpassingen, setAanpassingen] = useState({}); // { dateStr: { start, eind } | null (vrij) }
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Reset aanpassingen bij week/medewerker wissel
  useEffect(() => { setAanpassingen({}); setSuccess(''); setError(''); }, [weekOffset, selectedMwId]);

  // Week dates
  const weekDagen = useMemo(() => {
    const start = getWeekStart(new Date());
    start.setDate(start.getDate() + weekOffset * 7);
    const feestSet = new Set((feestdagen || []).map(f => f.datum || f.Datum));
    const dagen = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(start); d.setDate(d.getDate() + i);
      const dateStr = fmtDate(d);
      const dk = dagKeyVanDatum(d);
      const info = getWerkUrenDag(werkrooster, d);
      dagen.push({
        dateStr, datum: d, dagKey: dk,
        roosterStart: info.startTijd || '', roosterEind: info.eindTijd || '',
        roosterUren: info.uren || 0, werkt: info.werkt && !feestSet.has(dateStr),
        isFeestdag: feestSet.has(dateStr),
      });
    }
    return dagen;
  }, [weekOffset, werkrooster, feestdagen]);

  // Existing flex bookings
  const bestaandeBoekingen = useMemo(() => {
    const dates = new Set(weekDagen.map(d => d.dateStr));
    return verlofaanvragen.filter(v =>
      v.medewerkerId === targetId &&
      (v.Type === 'flex' || v.Type === 'flex-plus') &&
      v.Status !== 'afgekeurd' && dates.has(v.StartDatum)
    );
  }, [verlofaanvragen, targetId, weekDagen]);

  // Per-day computed info
  const dagInfo = useMemo(() => weekDagen.map(dag => {
    const aanp = aanpassingen[dag.dateStr];
    const bestaand = bestaandeBoekingen.find(b => b.StartDatum === dag.dateStr);
    if (!dag.werkt) return { ...dag, aangepast: false, aStart: '', aEind: '', nieuweUren: 0, delta: 0, bestaand };

    if (aanp !== undefined) {
      if (aanp === null) {
        // Vrij
        return { ...dag, aangepast: true, aStart: '', aEind: '', nieuweUren: 0, delta: -dag.roosterUren, bestaand };
      }
      const info = berekenUrenMetPauze(aanp.start, aanp.eind);
      const uren = Math.round(info.netto * 100) / 100;
      return { ...dag, aangepast: true, aStart: aanp.start, aEind: aanp.eind, nieuweUren: uren, delta: Math.round((uren - dag.roosterUren) * 100) / 100, bestaand };
    }
    return { ...dag, aangepast: false, aStart: dag.roosterStart, aEind: dag.roosterEind, nieuweUren: dag.roosterUren, delta: 0, bestaand };
  }), [weekDagen, aanpassingen, bestaandeBoekingen]);

  const totaalDelta = Math.round(dagInfo.reduce((s, d) => s + d.delta, 0) * 100) / 100;
  const totaalPlus = Math.round(dagInfo.filter(d => d.delta > 0).reduce((s, d) => s + d.delta, 0) * 100) / 100;
  const totaalMin = Math.round(dagInfo.filter(d => d.delta < 0).reduce((s, d) => s + Math.abs(d.delta), 0) * 100) / 100;
  const teIndienen = dagInfo.filter(d => d.aangepast && d.delta !== 0 && !d.bestaand);
  const heeftWijzigingen = teIndienen.length > 0;
  const isPast = (ds) => ds < new Date().toISOString().split('T')[0];

  // Handlers
  const updateDag = (dateStr, start, eind) => {
    setAanpassingen(prev => ({ ...prev, [dateStr]: { start, eind } }));
    setSuccess('');
  };
  const zetVrij = (dateStr) => {
    setAanpassingen(prev => ({ ...prev, [dateStr]: null }));
    setSuccess('');
  };
  const resetDag = (dateStr) => {
    setAanpassingen(prev => { const n = { ...prev }; delete n[dateStr]; return n; });
    setSuccess('');
  };
  const resetAlles = () => { setAanpassingen({}); setSuccess(''); setError(''); };

  // Presets
  const setPreset = (dateStr, dag, type) => {
    const rS = dag.roosterStart;
    const rE = dag.roosterEind;
    const rSm = timeToMin(rS);
    const rEm = timeToMin(rE);
    switch (type) {
      case 'ochtend-vrij': updateDag(dateStr, '13:00', rE); break;
      case 'middag-vrij': updateDag(dateStr, rS, '12:30'); break;
      case 'vrij': zetVrij(dateStr); break;
      case 'plus1': updateDag(dateStr, rS, minToTime(Math.min(rEm + 60, TL_END))); break;
      case 'plus2': updateDag(dateStr, rS, minToTime(Math.min(rEm + 120, TL_END))); break;
      case 'min1': updateDag(dateStr, rS, minToTime(Math.max(rEm - 60, rSm + SNAP))); break;
      case 'reset': resetDag(dateStr); break;
      default: break;
    }
  };

  // Submit
  const handleSubmit = async () => {
    if (teIndienen.length === 0) return;
    setSubmitting(true); setError('');
    try {
      for (const dag of teIndienen) {
        const isPlus = dag.delta > 0;
        const uren = Math.round(Math.abs(dag.delta) * 100) / 100;
        const submitData = {
          Type: isPlus ? 'flex-plus' : 'flex',
          StartDatum: dag.dateStr, EindDatum: dag.dateStr,
          StartTijd: dag.aStart || dag.roosterStart, EindTijd: dag.aEind || dag.roosterEind,
          Uren: uren,
          Reden: isPlus
            ? `Flex+: ${dag.aStart}–${dag.aEind} i.p.v. ${dag.roosterStart}–${dag.roosterEind}`
            : dag.nieuweUren === 0
              ? `Flex−: hele dag vrij (normaal ${dag.roosterStart}–${dag.roosterEind})`
              : `Flex−: ${dag.aStart}–${dag.aEind} i.p.v. ${dag.roosterStart}–${dag.roosterEind}`,
        };
        if (canManage && targetMw && targetMw.Id !== currentUser.id) {
          submitData.medewerkerId = targetMw.Id;
          submitData.medewerkerNaam = targetMw.Naam;
          submitData.directGoedkeuren = true;
        }
        // Verleden-inzendingen door admin/manager direct goedkeuren (ook eigen entries)
        if (canManage && isPast(dag.dateStr)) {
          submitData.forceGoedkeuren = true;
        }
        await indienVerlofaanvraag(submitData);
      }
      setSuccess(`${teIndienen.length} flexaanpassing${teIndienen.length === 1 ? '' : 'en'} ingediend!`);
      setAanpassingen({});
    } catch (err) { setError('Fout: ' + err.message); }
    finally { setSubmitting(false); }
  };

  const weekLabel = weekDagen.length > 0
    ? `Week ${getISOWeek(weekDagen[0].datum)} · ${weekDagen[0].datum.toLocaleDateString('nl-NL',{day:'numeric',month:'short'})} – ${weekDagen[4].datum.toLocaleDateString('nl-NL',{day:'numeric',month:'short',year:'numeric'})}`
    : '';

  const noTarget = canManage && !targetMw;

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={S.headerCard} className="r-welcome">
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#fff' }}>🔀 Flex Weekplanner</h2>
          <p style={{ fontSize: 13, opacity: 0.8, marginTop: 4, color: '#fff' }}>
            Sleep de werkbalk of gebruik de knoppen om je werktijden aan te passen.
          </p>
        </div>
      </div>

      {/* Manager/admin: medewerker selectie */}
      {canManage && (
        <div style={S.selectCard}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#323130', marginRight: 10 }}>Medewerker:</label>
          <select style={S.select} value={selectedMwId} onChange={e => setSelectedMwId(e.target.value)}>
            <option value="">— Kies medewerker —</option>
            {flexMedewerkers.map(m => <option key={m.Id} value={m.Id}>{m.Naam}</option>)}
          </select>
          {flexMedewerkers.length === 0 && (
            <span style={{ fontSize: 12, color: '#a19f9d', marginLeft: 10 }}>Geen medewerkers met flex profiel</span>
          )}
        </div>
      )}

      {!noTarget && (
        <>
          {/* Week nav */}
          <div style={S.weekNav}>
            <button style={S.navBtn} onClick={() => setWeekOffset(w => w - 1)}>‹</button>
            <div style={S.weekLabel}>{weekLabel}</div>
            <button style={S.navBtn} onClick={() => setWeekOffset(w => w + 1)}>›</button>
            {weekOffset !== 0 && <button style={{ ...S.navBtn, color: '#E86E1C', fontWeight: 700 }} onClick={() => setWeekOffset(0)}>Vandaag</button>}
          </div>

          {/* Day rows */}
          <div style={S.dayList}>
            {dagInfo.map(dag => {
              const past = isPast(dag.dateStr);
              // Admins/managers mogen ook in het verleden invoeren
              const locked = (past && !canManage) || !!dag.bestaand || dag.isFeestdag || !dag.werkt;
              const isAangepast = dag.aangepast && dag.delta !== 0;

              return (
                <div key={dag.dateStr} style={{
                  ...S.dayRow,
                  borderLeftColor: isAangepast ? (dag.delta > 0 ? '#00B7C3' : '#E86E1C') : dag.bestaand ? '#0078D4' : 'transparent',
                  background: isAangepast ? '#fefefe' : '#fff',
                  opacity: (past && !canManage && !dag.bestaand) ? 0.5 : 1,
                }}>
                  {/* Day label */}
                  <div style={S.dayLabel}>
                    <div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>{DAG_LABELS[dag.dagKey]}</span>
                      <span style={{ fontSize: 12, color: '#a19f9d', marginLeft: 6 }}>{fmtDK(dag.dateStr)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {dag.isFeestdag && <span style={S.badge('#D13438')}>Feestdag</span>}
                      {!dag.werkt && !dag.isFeestdag && <span style={S.badge('#a19f9d')}>Vrij</span>}
                      {dag.bestaand && (
                        <span style={S.badge(dag.bestaand.Type === 'flex-plus' ? '#00B7C3' : '#E86E1C')}>
                          {dag.bestaand.Type === 'flex-plus' ? '+' : '−'}{dag.bestaand.Uren}u
                          {dag.bestaand.Status === 'ingediend' ? ' ⏳' : ' ✓'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Timeline slider */}
                  {dag.werkt && !dag.isFeestdag && (
                    <TimelineSlider
                      roosterStart={dag.roosterStart}
                      roosterEind={dag.roosterEind}
                      start={dag.aangepast ? dag.aStart : dag.roosterStart}
                      eind={dag.aangepast ? dag.aEind : dag.roosterEind}
                      onChange={(s, e) => updateDag(dag.dateStr, s, e)}
                      disabled={locked}
                    />
                  )}

                  {/* Preset buttons + delta */}
                  {dag.werkt && !dag.isFeestdag && !locked && (
                    <div style={S.presetRow}>
                      <div style={S.presetBtns}>
                        <button style={S.preset('#E86E1C')} onClick={() => setPreset(dag.dateStr, dag, 'ochtend-vrij')}>Ochtend vrij</button>
                        <button style={S.preset('#E86E1C')} onClick={() => setPreset(dag.dateStr, dag, 'middag-vrij')}>Middag vrij</button>
                        <button style={S.preset('#D13438')} onClick={() => setPreset(dag.dateStr, dag, 'vrij')}>🏠 Vrij</button>
                        <button style={S.preset('#00B7C3')} onClick={() => setPreset(dag.dateStr, dag, 'plus1')}>+1u</button>
                        <button style={S.preset('#00B7C3')} onClick={() => setPreset(dag.dateStr, dag, 'plus2')}>+2u</button>
                        <button style={S.preset('#605e5c')} onClick={() => setPreset(dag.dateStr, dag, 'min1')}>−1u</button>
                        {dag.aangepast && <button style={S.preset('#0078D4')} onClick={() => resetDag(dag.dateStr)}>↩ Reset</button>}
                      </div>
                      {isAangepast && (
                        <div style={{
                          fontSize: 14, fontWeight: 800, padding: '4px 12px', borderRadius: 8,
                          background: dag.delta > 0 ? '#00B7C310' : '#E86E1C10',
                          color: dag.delta > 0 ? '#00B7C3' : '#E86E1C',
                          whiteSpace: 'nowrap',
                        }}>
                          {dag.delta > 0 ? '+' : ''}{dag.delta}u
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Submit bar */}
          {heeftWijzigingen && (
            <div style={S.submitCard}>
              <div style={S.summaryRow}>
                {totaalPlus > 0 && <span style={{ color: '#00B7C3', fontWeight: 700 }}>➕ +{totaalPlus}u</span>}
                {totaalMin > 0 && <span style={{ color: '#E86E1C', fontWeight: 700 }}>➖ −{totaalMin}u</span>}
                <span style={{ fontWeight: 800, fontSize: 18, color: totaalDelta >= 0 ? '#107C10' : '#D13438' }}>
                  Netto: {totaalDelta > 0 ? '+' : ''}{totaalDelta}u
                </span>
              </div>
              {error && <div style={S.errorBox}>⚠️ {error}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button style={S.resetBtn} onClick={resetAlles}>↩ Reset alles</button>
                <button style={S.submitBtn} onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Bezig...' : `Indienen (${teIndienen.length} dag${teIndienen.length === 1 ? '' : 'en'})`}
                </button>
              </div>
            </div>
          )}

          {success && <div style={S.successBox}>✅ {success}</div>}

          {/* Info */}
          <div style={S.infoCard}>
            <strong>Hoe werkt het?</strong> Sleep de werkbalk naar links/rechts om werktijden aan te passen,
            of gebruik de knoppen. Het verschil met je standaardrooster wordt automatisch als flex-plus of flex-min geregistreerd.
            Witte handgrepen aan beide kanten van de balk om de start- en eindtijd te slepen.
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  container: { display: 'flex', flexDirection: 'column', gap: 12 },
  headerCard: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
    borderRadius: 16, padding: '20px 24px',
  },
  selectCard: {
    background: '#fff', borderRadius: 12, padding: '12px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
  },
  select: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #c8c6c4',
    fontSize: 14, color: '#323130', minWidth: 200,
  },
  weekNav: { display: 'flex', alignItems: 'center', gap: 6 },
  navBtn: {
    background: '#fff', border: '1px solid #edebe9', borderRadius: 8,
    padding: '6px 12px', fontSize: 15, fontWeight: 600, cursor: 'pointer', color: '#323130',
    width: 36, textAlign: 'center',
  },
  weekLabel: { flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#1a1a2e' },
  dayList: { display: 'flex', flexDirection: 'column', gap: 6 },
  dayRow: {
    background: '#fff', borderRadius: 12, padding: '12px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)', borderLeft: '4px solid transparent',
  },
  dayLabel: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2,
  },
  badge: (c) => ({
    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
    background: `${c}15`, color: c, whiteSpace: 'nowrap',
  }),
  presetRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, gap: 8, flexWrap: 'wrap',
  },
  presetBtns: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  preset: (c) => ({
    background: `${c}10`, color: c, border: `1px solid ${c}30`, borderRadius: 6,
    padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  }),
  submitCard: {
    background: '#fff', borderRadius: 12, padding: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '2px solid #E86E1C',
  },
  summaryRow: { display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' },
  resetBtn: {
    padding: '8px 18px', borderRadius: 8, border: '1px solid #c8c6c4',
    background: '#fff', fontSize: 13, fontWeight: 600, color: '#605e5c', cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 24px', borderRadius: 8, border: 'none',
    background: '#E86E1C', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer',
  },
  errorBox: { background: '#fde7e9', border: '1px solid #f1bbbc', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#a4262c', marginBottom: 10 },
  successBox: { background: '#dff6dd', border: '1px solid #a7e3a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#107C10' },
  infoCard: { background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 12, padding: '12px 16px', fontSize: 12, color: '#605e5c', lineHeight: 1.6 },
};
